import { connect } from "react-redux";
import CoupleHeading from "../../CoupleHeading/CoupleHeading";
import Hashtag from "../../Hashtag/Hashtag";
import { getCloudinaryFullPath } from "../../../utils";
import { heroIllustration, isJainInvite } from "../helpers";
import Reveal from "../Reveal";
import Section from "./Section";
import Person from "../../Person/Person";

/** A photo in an arched jharokha window, a marigold at its crown. */
export const ArchFrame = ({ src, alt, className = "" }) => (
  <div className={`ut-arch-frame ${className}`}>
    <div className="ut-arch-frame__window">
      <img src={src} alt={alt} loading="lazy" decoding="async" />
    </div>
    <svg className="ut-arch-frame__crown" viewBox="-12 -12 24 24" aria-hidden="true">
      <use href="#ut-marigold" x="-11" y="-11" width="22" height="22" />
    </svg>
  </div>
);

/**
 * Where the couple's illustration comes to rest at the top of the page, above its heading
 * (so nothing lies in their path), flown in from the
 * first page's courtyard (CoupleFlight.js): a haldi halo and a slowly turning ring of
 * marigolds open up behind them as they arrive (`--p`, 0 to 1), and they stand on a soft shadow.
 * The image here holds the place and marks where the flight lands; it is shown itself
 * only when nothing flies (reduced motion).
 */
const CoupleStage = ({ src }) => (
  <div className="ut-couple__stage">
    <div className="ut-couple__halo" aria-hidden="true" />
    <svg className="ut-couple__ring" viewBox="-100 -100 200 200" aria-hidden="true">
      <circle r="97" fill="none" stroke="#c9962b" strokeOpacity="0.55" strokeWidth="1.2" />
      <circle r="89" fill="none" stroke="#d81b60" strokeOpacity="0.4" strokeWidth="2.4" strokeLinecap="round" strokeDasharray="0 9.32" />
      {Array.from({ length: 8 }, (_, i) => (
        <use key={i} href="#ut-marigold" x="-7" y="-104" width="14" height="14" transform={`rotate(${i * 45})`} />
      ))}
    </svg>
    <div className="ut-couple__ground" aria-hidden="true" />
    <div className="ut-couple__figure">
      <img src={src} alt="" decoding="async" />
    </div>
  </div>
);

// The bride and groom (shared components/Person) in Rang Utsav's arched jharokha frames
const UT_PERSON = { prefix: "ut", Reveal, Frame: ArchFrame };

/**
 * The couple's illustration (flown in from the first page), the blessing, their welcome
 * line and hashtag, and the bride & groom.
 * A couple photo, when added, is shown once between the pair in place of their own
 * portraits; otherwise each shows their own photo, if any.
 */
const Couple = ({ bride, groom, couplePhoto, religion, isFromPortfolio, illustration }) => (
  <Section
    id="couple"
    className="ut-couple"
    label="The bride and groom"
    deco={["toran", "latkans", "butterflies"]}
    // kicker={isJainInvite(religion) ? "|| Shree Mahaveeray Namah ||" : "|| Shree Ganeshay Namah ||"}
    top={illustration && <CoupleStage src={illustration} />}
  >
    <Reveal className="ut-couple__welcome" delay={0.1}>
      <CoupleHeading />
      <Hashtag />
    </Reveal>

    <div className="ut-couple__pair">
      <Person {...UT_PERSON} data={bride || {}} isGroom={false} hidePhone={isFromPortfolio} hidePhoto={!!couplePhoto} delay={0.1} />
      {couplePhoto ? (
        <Reveal effect="zoom" className="ut-couple__photo" delay={0.1}>
          <ArchFrame className="ut-couple__portrait" src={getCloudinaryFullPath(couplePhoto)} alt="The bride and groom" />
        </Reveal>
      ) : (
        <Reveal effect="fade" className="ut-couple__and" delay={0.3}>
          <span className="ut-script">&amp;</span>
        </Reveal>
      )}
      <Person {...UT_PERSON} data={groom || {}} isGroom hidePhone={isFromPortfolio} hidePhoto={!!couplePhoto} delay={0.2} />
    </div>
  </Section>
);

const mapStateToProps = (state) => ({
  bride: state.inviteDetails.invite?.bride,
  groom: state.inviteDetails.invite?.groom,
  couplePhoto: state.inviteDetails.invite?.couplePhoto,
  religion: state.inviteDetails.invite?.religion,
  isFromPortfolio: state.common?.isFromPortfolio,
  illustration: heroIllustration(state.inviteDetails.invite),
});

export default connect(mapStateToProps)(Couple);
